import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"

import {
  AllProjectPermissionsDocument,
  ProjectsDocument
} from "entities/projects/graphql/queries.generated"
import { TeamMember } from "entities/team-members/types/TeamMember"

import { TeamInvitationFragmentDoc } from "../graphql/fragments.generated"
import {
  AcceptTeamInvitationDocument,
  AcceptTeamInvitationMutation,
  AcceptTeamInvitationMutationVariables
} from "../graphql/mutations.generated"
import { useTeamInvitationsLazyQuery } from "../graphql/queries.generated"
import { extractUuidsFromInvitationAcceptPath } from "../lib/extractUuidsFromInvitationAcceptPath"
import { TeamInvitation, TeamInvitationTypename } from "../types/TeamInvitation"

export function useAcceptTeamInvitation() {
  const { cache } = useApolloClient()
  const { t } = useTranslation()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      success: t("Request.TeamInvitation.AcceptTeamInvitation.Success"),
      failure: t("Request.TeamInvitation.AcceptTeamInvitation.Failure"),
      loading: t("Request.TeamInvitation.AcceptTeamInvitation.Start")
    }
  })
  const [refetchPendingInvitations] = useTeamInvitationsLazyQuery({
    variables: { pending: true }
  })
  const [apolloMutate] = useApolloMutation<
    AcceptTeamInvitationMutation,
    AcceptTeamInvitationMutationVariables
  >(AcceptTeamInvitationDocument)

  const acceptTeamInvitation = useEvent(
    async (teamInvitation: TeamInvitation) => {
      const { teamID, teamMemberID } = extractUuidsFromInvitationAcceptPath(
        teamInvitation.acceptInvitationPath
      )
      const { raiseErrorToast, raiseSuccessToast } = raiseLoadingToast()

      const result = await callMutation(apolloMutate, {
        variables: {
          teamID,
          teamMemberID,
          input: { id: teamInvitation.id, invitationAccepted: true }
        },
        transform: data => data.acceptTeamInvitation as TeamMember,
        apolloOptions: {
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          },
          refetchQueries: [
            { query: ProjectsDocument },
            { query: AllProjectPermissionsDocument }
          ]
        }
      })

      if (result.data?.invitationAccepted) {
        cache.updateFragment(
          {
            id: cache.identify({
              id: teamInvitation.id,
              __typename: TeamInvitationTypename
            }),
            fragmentName: "TeamInvitation",
            fragment: TeamInvitationFragmentDoc
          },
          data => ({
            ...data,
            invitationAccepted: true
          })
        )
      }

      await refetchPendingInvitations({
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })

      return result
    }
  )

  return { acceptTeamInvitation }
}
